// ================= THE LIBRARY =================
//
// Every card this player can build with, on one screen, with the season's store inside it rather
// than beside it. The user's decision, and their reason is the one that shaped the whole file:
// *the player should watch their library change as they buy.* A locked row that unlocks in place
// is a row that stops being grey; a library that gains a card is an event.
//
// So the two halves are arranged to make that event visible. The shelf at the top holds the cards
// of the season this player does NOT own yet; the collection below holds everything they do. A
// purchase moves a card from the first to the second, the season heading counts up, and the
// collection's own total goes up by one. Nothing is greyed out and nothing is struck through,
// because nothing here is a card the player cannot have — it is a card they have not bought yet.
//
// THREE RULES THIS SCREEN EXISTS TO KEEP, all three from deploy_handoff/scenes-from-summer-plan.md:
//
//   NO SESSION, NO STORE, AND NO LOCKS EITHER. On `file://`, on a build with the accounts backend
//   unconfigured, and for a visitor who has not signed in, the seasonal cards are ABSENT — not
//   listed with a padlock. A lock with no unlock path is an ad for a feature that build does not
//   have (the account-chip principle, settled in this repo before this screen existed). What a
//   signed-out visitor on a CONFIGURED site gets instead is the door itself: one line offering
//   sign-in. That is not a lock, and the distinction is the entire difference.
//
//   THE PURCHASE REPAINTS FROM THE SERVER'S ANSWER, NEVER FROM THE CLICK. `applyState` below is
//   the ONLY thing that writes STATE.owned and STATE.balance, and it is called with the page-load
//   read and with the purchase reply through the same door. There is no `owned.push(cardId)`
//   anywhere in this file, and there must never be one: a card added because a button was pressed
//   is a card the player may not own if the write lost its race with the balance.
//
//   IT IS NOT THE GAME SHELL. No ui.js, no ui-shell.js, no narrative modules, no engine AI — the
//   same posture js/deck-builder-ui.js records, for the same reason. What it borrows is the card
//   tile (js/card-tile.js) and the rules (js/deck-rules.js), so the collection a player browses
//   here is exactly the pool the builder will offer them.
(function () {
  'use strict';

  const R = window.DeckRules;
  const TIERS = ['Style', 'Family', 'Universal'];
  const esc = window.CardTile.escapeHtml;
  const el = id => document.getElementById(id);
  const cardsTable = () => window.CARDS;
  const label = key => (window.STYLE_LABELS || {})[key] || key;

  // THE ONE PLACE THE SERVER'S ANSWER LANDS. Everything on screen is derived from this object, and
  // nothing writes to it except applyState().
  const STATE = {
    asked: false,        // has a /api/store answer arrived at all (success or failure)
    configured: false,   // does this deployment have the accounts backend
    signedIn: false,
    owned: [],           // card ids, in the order they were bought
    balance: 0,
    catalog: [],         // [{ cardId, price }] — the SERVER'S catalog when there is one
    reachable: false,    // did the last /api/store call actually come back with an answer
    busy: null,          // the card id a purchase is in flight for, or null
    message: ''
  };

  const FILTERS = { search: '', style: 'all', mechanic: 'all' };

  // ---------------- The mechanic filter ----------------
  //
  // WHY A FILTER RATHER THAN MORE BADGES. Search already finds these -- the printed rules are
  // indexed, so typing "off-balance" works -- but a player has to know the phrase and know it is
  // worth typing, and neither is true of somebody who has met the mechanic once in a bout. A badge
  // would answer it at a glance, and a badge is what Chamber gets, because Chamber is eight cards.
  // These are not: Off-Balance is on 45 of 223, Openings 49, Flow or Fury 62. Badging those would
  // put a mark on a third of the pool and the badge row would stop carrying information.
  //
  // DERIVED FROM THE PRINTED TEXT, not from a hand-kept list of card ids, so a card that gains or
  // loses a clause moves between the buckets by itself. The one exception is Chamber, which is
  // asked of the engine's own tag: it is the only mechanic here whose participation is data rather
  // than prose, and a text match would also catch Rapid Rechamber, which is a card named after the
  // motion and has nothing to do with the resource.
  //
  // The counts beside each option are the point of the control as much as the filtering is: "45
  // cards do this" is the fact that tells a player the mechanic is worth learning.
  const MECHANIC_FILTERS = [
    { key: 'chamber', label: 'Chamber & Release',
      test: (card) => !!(window.ENGINE && window.ENGINE.cardUsesChamber(card)) },
    { key: 'offbalance', label: 'Off-Balance', re: /off-balance/i },
    { key: 'opening', label: 'Openings', re: /opening/i },
    { key: 'track', label: 'Flow & Fury', re: /\b(flow|fury)\b/i },
    { key: 'endrange', label: 'End Range', re: /\bEnd\b/ },
    { key: 'recovery', label: 'Stamina recovery', re: /recover/i }
  ];
  function cardHasMechanic(card, key) {
    const m = MECHANIC_FILTERS.find(x => x.key === key);
    if (!m || !card) return true;
    if (m.test) return m.test(card);
    const text = [(card.attack && card.attack.text) || '',
                  (card.defense && card.defense.text) || ''].join(' ');
    return m.re.test(text);
  }
  // 'rows' | 'tiles'. Set from the device on first load, then from the player's own choice.
  let VIEW = 'tiles';
  // WHICH CARDS THIS PAGE IS SHOWING. Only ever anything but 'collection' when there is an account
  // to have a collection distinct from the base set -- see renderTabs.
  let TAB = 'collection';

  // Keyed on the card id ALONE, which is the whole identity of what is drawn here: unlike the
  // builder's pool this screen belongs to no deck and no fighter, so the tile's silhouettes get no
  // art and the generic figure is drawn instead. That is the honest picture for a collection — a
  // card in your library is not yet anybody's.
  const TILE_CACHE = new Map();

  // ---------------- derived reads ----------------

  // Asked of StoreClient rather than re-derived, so "is there a server to reach at all" has one
  // definition in this codebase.
  function offline() { return window.StoreClient ? window.StoreClient.offline() : true; }

  // A Set every time rather than a cached one: it is rebuilt only on render, it is at most fifteen
  // entries, and a cached membership set that outlives the answer it was built from is precisely
  // the bug the "repaint from the server" rule exists to prevent.
  function ownedSet() { return new Set(STATE.owned); }

  // THE COLLECTION. Every card the player may build with — which is the base pool plus whatever
  // they have bought, and never a Challenge card: those belong to an AI deck and to nobody's
  // library, which is what the tier means.
  //
  // Asked through DeckRules.ownsCard rather than by testing the seasonal stamp here, so this screen
  // and the validator cannot come to disagree about what "yours" means.
  function collectionIds() {
    const C = cardsTable();
    const owned = ownedSet();
    return Object.keys(C).filter(id => {
      const card = C[id];
      if (TIERS.indexOf(card.ownershipTier) < 0) return false;
      return R.ownsCard(id, owned);
    });
  }

  // ---------------- the ownership tabs ----------------
  //
  // The audit: "Signed out page says 'Your Library' and 'Your cards 194' while seasonal ownership
  // depends on credits/account" -- a reader could not tell whether they were looking at what they
  // own or at what exists. Stage 2 fixed the HEADING; this splits the list behind it.
  //
  // ONLY WITH AN ACCOUNT. Signed out there is exactly one answer -- every card here is one the game
  // gives everybody -- so four tabs offering to slice it would be four ways to ask a question with
  // no second answer, and 'Seasonal' would be a tab that is always empty, which is a lock by
  // another name. That is the rule this page has kept since it was written.
  const TABS = [
    { id: 'collection', label: 'Collection', hint: 'Everything you can build with' },
    { id: 'base', label: 'Base set', hint: 'The cards every player has' },
    { id: 'seasonal', label: 'Seasonal', hint: 'The season cards you own' },
    { id: 'all', label: 'All', hint: 'Including cards you have not bought' }
  ];

  // The ids a tab shows. 'all' is the only one that reaches past what the player owns, and the
  // cards it adds arrive wearing the season's badge and open a sheet that offers to sell them --
  // which is the difference between showing a card and locking one.
  function tabIds() {
    const C = cardsTable();
    const owned = ownedSet();
    const mine = collectionIds();
    if (!storeIsOpen() || TAB === 'collection') return mine;
    if (TAB === 'base') return mine.filter(id => !C[id].seasonal);
    if (TAB === 'seasonal') return mine.filter(id => C[id].seasonal);
    // 'all' -- the collection plus the season's unbought cards, in the same groups.
    return mine.concat(STATE.catalog.map(e => e.cardId)
      .filter(id => C[id] && !owned.has(id)));
  }

  function setTab(id) {
    TAB = TABS.some(t => t.id === id) ? id : 'collection';
    // BOTH, and this repainted only the list. Tapping a tab changed which cards were shown and left
    // the strip lighting the tab you had just left -- the chip's `is-on` class and its aria-pressed
    // are both written by renderTabs, so the page said "Collection" while listing All. It passed
    // every stage-4 assertion because those all read the LIST. Found by the accessibility sweep
    // asking the chips what they claimed.
    renderTabs();
    renderCollection();
  }

  // A TAB EARNS ITS PLACE BY SLICING THE LIST DIFFERENTLY, and three of these four spend most of
  // their life not doing that. Reported from a real account: "All is just the same as Collection".
  // It was, and for a good reason -- that player owns the whole season, so there is nothing left
  // for All to reach past the collection to. The same arithmetic makes two more of them redundant
  // in the opposite case:
  //
  //   Base     is the collection minus the seasonal cards. Own none and it IS the collection.
  //   Seasonal is the seasonal cards you own. Own none and it is empty.
  //   All      is the collection plus what is still for sale. Own everything and it IS the
  //            collection.
  //
  // So a new signed-in player saw four tabs, two of which repeated the list they were already
  // looking at and one of which was blank; a completionist saw four, one of which repeated it. A
  // control that does nothing teaches that controls here do nothing -- the same reasoning that
  // keeps this page from showing a lock it cannot open.
  //
  // Computed from the same two facts everything else here reads, so it cannot drift from what the
  // tabs would actually show.
  function availableTabs() {
    const C = cardsTable();
    const owned = ownedSet();
    const ownsSeasonal = collectionIds().some(id => C[id].seasonal);
    const unbought = STATE.catalog.map(e => e.cardId).filter(id => C[id] && !owned.has(id)).length;
    return TABS.filter(t =>
      t.id === 'collection' ||
      (t.id === 'base' && ownsSeasonal) ||
      (t.id === 'seasonal' && ownsSeasonal) ||
      (t.id === 'all' && unbought > 0));
  }

  function renderTabs() {
    const box = el('lib-tabs');
    if (!box) return;
    if (!storeIsOpen()) { box.hidden = true; box.innerHTML = ''; TAB = 'collection'; return; }
    const C = cardsTable();
    const owned = ownedSet();
    const shown = availableTabs();
    // ONE ANSWER NEEDS NO TABS TO SLICE IT -- the same sentence the signed-out case has always
    // been held to. A strip carrying a single chip is a label wearing a control's clothes.
    if (shown.length < 2) { box.hidden = true; box.innerHTML = ''; TAB = 'collection'; return; }
    // A tab can stop existing under the player: buying the last card in the season retires All
    // while they are standing on it. Fall back rather than render a list nothing is pressed for.
    if (!shown.some(t => t.id === TAB)) TAB = 'collection';
    const counts = {
      collection: collectionIds().length,
      base: collectionIds().filter(id => !C[id].seasonal).length,
      seasonal: collectionIds().filter(id => C[id].seasonal).length,
      all: collectionIds().length +
           STATE.catalog.map(e => e.cardId).filter(id => C[id] && !owned.has(id)).length
    };
    box.hidden = false;
    box.innerHTML = shown.map(t =>
      '<button type="button" class="lib-tab' + (TAB === t.id ? ' is-on' : '') + '"' +
      ' data-act="tab" data-tab="' + esc(t.id) + '"' +
      ' aria-pressed="' + (TAB === t.id) + '" title="' + esc(t.hint) + '">' +
      esc(t.label) + ' <span class="lib-tab-n">' + counts[t.id] + '</span></button>').join('');
  }

  // The season's cards that are still for sale to this player. Empty when there is nothing to sell
  // OR nothing left to buy, and the shelf says which of those two it is.
  function forSaleIds() {
    const owned = ownedSet();
    return STATE.catalog.map(e => e.cardId)
      .filter(id => cardsTable()[id] && !owned.has(id));
  }

  function priceOf(id) {
    for (let i = 0; i < STATE.catalog.length; i++) {
      if (STATE.catalog[i].cardId === id) return STATE.catalog[i].price;
    }
    return null;
  }

  // Is there a store on this screen at all? Both halves are required and they fail differently:
  // an unconfigured deployment has no store to offer anyone, and a signed-out visitor has no
  // account to buy into. Neither is shown a locked card.
  function storeIsOpen() { return STATE.configured && STATE.signedIn; }

  // Group heads, in the order a player thinks about them: the eight Styles, then the four Families
  // that pair them, then the cards every deck may hold. Read off window.STYLES / window.FAMILIES so
  // a ninth Style appears here by existing rather than by being added to a list in this file.
  function groupOrder() {
    return (window.STYLES || []).concat(window.FAMILIES || []).concat(['Universal']);
  }

  function groupOf(card) {
    return card.ownershipTier === 'Universal' ? 'Universal' : card.ownershipGroup;
  }

  // THE STYLE FILTER ANSWERS A DECK-BUILDING QUESTION, so it is answered by the deck builder's own
  // function. "What do I have for a Judo deck" is legalPool(style, owned) exactly — Style cards of
  // that Style, Family cards of its Family, and every Universal card. Re-deriving that here would
  // be a second implementation of the pool rule, which is the thing js/deck-rules.js exists to stop.
  function filterSet() {
    if (FILTERS.style === 'all') return null;
    const pool = R.legalPool(FILTERS.style, ownedSet());
    const set = new Set();
    TIERS.forEach(t => pool[t].forEach(id => set.add(id)));
    return set;
  }

  // THE PRINTED RULES ARE SEARCHED, and this used to be name + id + moveGloss -- flavour, not
  // rules. A player looking for the Off-Balance cards typed "off-balance" and got one of the
  // forty-five that say it. See MechanicsDictionary.searchText for what is indexed and why the
  // deck builder asks the same question of the same function.
  //
  // Guarded, like every other MechanicsDictionary call on this page: with the module absent the
  // old behaviour is what is left, which is a worse search rather than no list.
  function matchesSearch(id) {
    if (!FILTERS.search) return true;
    const card = cardsTable()[id];
    const M = window.MechanicsDictionary;
    if (M && M.matchesSearch) return M.matchesSearch(card, FILTERS.search);
    const q = FILTERS.search.toLowerCase();
    return (card.name + ' ' + id + ' ' + (card.moveGloss || '')).toLowerCase().indexOf(q) >= 0;
  }

  // ---------------- the bounded first render ----------------
  //
  // The audit's largest single finding: this page mounted every card at once -- 194 tiles, 21,939
  // DOM elements, a 9,760px document -- and rebuilt the whole thing on every search keystroke.
  //
  // Groups are rendered a few at a time and the rest follow as the reader approaches them. Two
  // things make this safe rather than clever:
  //
  //   THE QUEUE IS THE TRUTH. Everything is already computed and sorted; only its insertion is
  //   deferred. Nothing about which cards exist, or the count reported above the list, depends on
  //   how far the reader has scrolled.
  //
  //   NO OBSERVER MEANS RENDER EVERYTHING. jsdom has no IntersectionObserver, and neither does a
  //   browser old enough to matter -- and a Cards page that silently rendered nothing there would
  //   pass every suite in this repo while shipping a blank page. Absence of the API is a reason to
  //   do the simple thing, not the clever one.
  const EAGER_GROUPS = 2;
  let PENDING = [];
  let WATCHING = false;

  // HOW CLOSE COUNTS. Enough that a group is in the DOM before its space is, rather than arriving
  // as a visible pop under the reader's thumb.
  const REACH_PX = 600;

  function drainInto(box, howMany) {
    const old = box.querySelector('.lib-sentinel');
    if (old) old.remove();
    const n = (howMany === Infinity) ? PENDING.length : Math.min(howMany, PENDING.length);
    if (n > 0) box.insertAdjacentHTML('beforeend', PENDING.splice(0, n).join(''));
    if (!PENDING.length) { stopWatching(); return; }
    box.insertAdjacentHTML('beforeend', '<div class="lib-sentinel" aria-hidden="true"></div>');
    startWatching();
    // The first paint may already have the sentinel in reach -- a short filter result, a tall
    // window -- and waiting for a scroll that never comes would strand the rest of the list.
    maybeDrain();
  }

  // A SCROLL LISTENER, NOT AN IntersectionObserver, and the reason is that the observer could not
  // be VERIFIED here. Its callbacks are delivered on the rendering lifecycle, which headless
  // Chrome under --virtual-time-budget --dump-dom does not drive: the sentinel sat plainly inside
  // the viewport, 816px down an 844px screen, and nothing fired. Whether that would have worked in
  // a real browser is exactly the question a check cannot answer, and the bounded render is the
  // whole point of this change -- so it uses a mechanism the harness can drive.
  //
  // The cost is one listener doing one getBoundingClientRect per frame while there is anything
  // left to mount, and it unbinds itself the moment the queue empties.
  // SYNCHRONOUS, deliberately. The first version throttled through requestAnimationFrame, which
  // is the textbook shape and was the second mechanism in a row that could not be verified here --
  // under --virtual-time-budget the frame never arrives and the drain never runs. It is one
  // getBoundingClientRect on one element, guarded so it cannot re-enter, and it unbinds the moment
  // the queue empties; a rAF hop buys nothing measurable and costs the ability to test it.
  let DRAINING = false;
  function onScroll() {
    if (DRAINING) return;
    DRAINING = true;
    try { maybeDrain(); } finally { DRAINING = false; }
  }

  function maybeDrain() {
    const box = el('lib-list');
    if (!box || !PENDING.length) return;
    const sentinel = box.querySelector('.lib-sentinel');
    if (!sentinel) return;
    const top = sentinel.getBoundingClientRect().top;
    const h = window.innerHeight || document.documentElement.clientHeight || 0;
    if (top <= h + REACH_PX) drainInto(box, 1);
  }

  function startWatching() {
    if (WATCHING) return;
    WATCHING = true;
    // BOTH targets: a document scroll fires at `document` and reaches `window`, but this page is
    // also laid out so that a container can be the scroller depending on width. Listening in the
    // capture phase on document catches either without caring which.
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
  }

  function stopWatching() {
    if (!WATCHING) return;
    WATCHING = false;
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onScroll);
  }

  // Used by the group jump: a group the reader asked for by name has to exist before it can be
  // scrolled to, however far down the queue it is.
  function drainAll() {
    const box = el('lib-list');
    if (box && PENDING.length) drainInto(box, Infinity);
  }

  // ---------------- rendering ----------------

  // ROWS OR TILES. Rows are the phone default: the audit measured this page at 194 tiles and
  // 21,939 DOM elements, and on a phone the grid resolves to one wide card per row, which is a
  // catalogue rather than a collection you can find anything in. The tile stays available as a
  // toggle because it is the better shape for browsing on a desktop, where the width exists.
  //
  // The device answers first and an explicit choice overrides it forever. Same media query
  // UIShell.isPhoneDevice uses -- restated rather than shared because this page deliberately does
  // not load the game shell (see the script comment in library.html).
  const VIEW_KEY = 'sc.library.view';
  function preferredView() {
    let saved = null;
    try { saved = window.localStorage.getItem(VIEW_KEY); } catch (e) { /* no storage: ask the device */ }
    if (saved === 'rows' || saved === 'tiles') return saved;
    const mq = window.matchMedia && window.matchMedia('(max-width: 700px) and (pointer: coarse)');
    return (mq && mq.matches) ? 'rows' : 'tiles';
  }
  function setView(view) {
    VIEW = (view === 'rows') ? 'rows' : 'tiles';
    try { window.localStorage.setItem(VIEW_KEY, VIEW); } catch (e) { /* the choice just does not persist */ }
    // BOTH lists: the shelf follows the same shape, and repainting only the collection left the
    // two halves of this page in different modes.
    renderStore();
    renderCollection();
  }

  // THE CACHE KEY CARRIES THE MODE. It did not, and could not have needed to, while there was one
  // renderer; with two, a card rendered as a row and then asked for as a tile would have been
  // served the row.
  function cardHtml(id, opts) {
    const key = id + '|' + ((opts && opts.stateText) || '') + '|' + VIEW;
    if (!TILE_CACHE.has(key)) {
      TILE_CACHE.set(key, VIEW === 'rows'
        ? window.CardTile.compactRowHtml(id, Object.assign({
            onclick: 'Library.openCard(\'' + id + '\')'
          }, opts || {}))
        // NOT `readonly` any more. The tiles were focus-skipped, aria-disabled buttons holding
        // detailed content -- the audit's "194 disabled buttons" -- which was defensible only
        // while tapping one did nothing. Tapping one opens its details now, so it is a control.
        : window.CardTile.compactCardHtml(id, Object.assign({
            moveArt: true, onclick: 'Library.openCard(\'' + id + '\')'
          }, opts || {})));
    }
    return TILE_CACHE.get(key);
  }

  // THE SHELF FOLLOWS THE COLLECTION'S SHAPE. It began as always-tiles on the argument that the
  // full card is the sales display -- which is true on a desktop and was the wrong call on a
  // phone: with the collection compacted to rows, the shelf became the last full-tile list on the
  // page and a signed-in player scrolled roughly 2,900px of it before reaching their own cards.
  // A shop you have to scroll past to reach what you came for is not selling harder, it is in the
  // way. Tapping a row opens the same sheet, which carries the art, the rules AND the Buy.
  //
  // Its own cache key either way, because the shelf's tile is asked for without the collection's
  // click handler.
  function shelfCardHtml(id) {
    const key = 'shelf|' + id + '|' + VIEW;
    if (!TILE_CACHE.has(key)) {
      TILE_CACHE.set(key, VIEW === 'rows'
        // NO SEASON BADGE HERE, unlike the same card in the collection below. There the badge is
        // what makes a purchase visible among two hundred cards; on this shelf every row is a
        // season card and the heading above says so, and the badge was taking the width the NAME
        // needs -- it rendered as "C...", "R...", "S...". A label that pushes out the thing it
        // labels is not earning its place.
        ? window.CardTile.compactRowHtml(id, {
            onclick: "Library.openCard('" + id + "')"
          })
        // OPENABLE, exactly like the same card in the collection below. It shipped `readonly`, so
        // on the shelf's TILE view -- which is the desktop default -- a card for sale was the one
        // card on this page whose rules could not be read. You could buy it and then read it. The
        // sheet already knows how to sell (openCard puts Buy in it when the card is for sale), so
        // this is one option, not a second purchase path.
        : window.CardTile.compactCardHtml(id, {
            moveArt: true, onclick: "Library.openCard('" + id + "')"
          }));
    }
    return TILE_CACHE.get(key);
  }

  function byName(a, b) {
    const C = cardsTable();
    return String((C[a] || {}).name || a).localeCompare(String((C[b] || {}).name || b));
  }

  // ---- the standing strip -----------------------------------------------------------------------
  //
  // What it says depends on which of three worlds the page is in, and the three are deliberately
  // different sentences rather than one sentence with blanks: a build with no accounts must not
  // read as an account that has nothing in it.
  function renderStanding() {
    const total = collectionIds().length;
    const seasonal = STATE.catalog.length;
    const ownedSeasonal = STATE.owned.length;

    let html = '<span class="lib-stat"><b>' + total + '</b> cards</span>';

    if (storeIsOpen()) {
      html += '<span class="lib-stat"><b>' + STATE.balance + '</b> credits</span>';
      if (seasonal) {
        html += '<span class="lib-stat lib-stat--season"><b>' + ownedSeasonal + '</b> of ' +
                seasonal + ' seasonal cards</span>';
      }
    } else if (STATE.configured && STATE.asked) {
      // THE DOOR, NOT A LOCK. No card is listed, no price is shown and nothing on screen is greyed
      // out; this is one sentence saying the account exists and where it leads. A signed-out
      // visitor on a site that HAS a season should not have to guess that it does.
      const href = (window.DeckSync && window.DeckSync.SIGN_IN_URL) || null;
      html += href
        ? '<span class="lib-signin"><a href="' + esc(href) + '">Sign in with Discord</a>' +
          ' to earn credits and collect the season\'s cards.</span>'
        : '';
    } else if (STATE.asked && !STATE.reachable && !offline()) {
      // THE STORE IS THERE AND DID NOT ANSWER, which is NOT the same as a build that has no store —
      // and until this branch existed the screen could not tell them apart. Both left `configured`
      // false, so a 500 from /api/store rendered as a perfectly ordinary library with no season in
      // it: no error, no message, nothing to report to anybody. A player would conclude the season
      // does not exist on this build, which is the one wrong conclusion available.
      //
      // Only on http(s): on `file://` there is genuinely no server and silence is the honest answer.
      html += '<span class="lib-offline">The store is not answering just now. ' +
              'Your cards are all here; nothing can be bought until it is back.</span>';
    }
    el('lib-standing').innerHTML = html;
  }

  // ---- the season shelf --------------------------------------------------------------------------
  function buyRowHtml(id) {
    const card = cardsTable()[id];
    const price = priceOf(id);
    const short = price != null && STATE.balance < price;
    const busy = STATE.busy === id;
    const disabled = busy || !!STATE.busy || short;
    // A disabled control with no stated reason reads as a bug — the builder's rule, and it applies
    // harder here because the reason is a number the player can do something about.
    const why = short ? 'You need ' + (price - STATE.balance) + ' more credits'
              : busy ? 'Buying…'
              : STATE.busy ? 'Another purchase is in flight'
              : 'Buy ' + card.name + ' for ' + price + ' credits';
    return '<div class="db-row lib-row" data-card-id="' + esc(id) + '">' +
      '<div class="db-row-tile">' + shelfCardHtml(id) + '</div>' +
      '<div class="db-row-ctl lib-buy">' +
        '<span class="lib-price' + (short ? ' is-short' : '') + '">' + (price == null ? '—' : price) +
          '<span>cr</span></span>' +
        '<button type="button" class="db-btn db-btn--primary" data-act="buy" data-card-id="' +
          esc(id) + '"' + (disabled ? ' disabled' : '') + ' title="' + esc(why) + '" aria-label="' +
          esc(why) + '">' + (busy ? '…' : 'Buy') + '</button>' +
      '</div></div>';
  }

  // WHAT THE SHELF COSTS, AND WHAT EARNS IT. deploy_handoff/retention-plan.md R2.
  //
  // EVERY NUMBER READ FROM A MIRROR, none typed. js/credit-rules.js is held equal to
  // lib/credits/rules.js by tests/credit-rules-test.js and js/store-catalog.js to lib/store/
  // catalog.js by tests/store-catalog-test.js, so a sentence composed from them cannot drift away
  // from what the server actually pays and charges. The version this replaced said "100 credits
  // each" as text, which was correct and was correct by luck.
  //
  // THE PRICE COMES FROM STATE.catalog FIRST, because that is the server's own answer for this
  // player and this season — the mirror is the fallback for a page that has not heard from it.
  // Falls back again to the markup's own signed-out sentence if neither mirror is loaded, which is
  // the file:// case and the case where a script tag went missing.
  function ratesSentence() {
    const R = window.CREDIT_RULES;
    if (!R) return null;
    const prices = STATE.catalog.map(e => e.price).filter(p => typeof p === 'number');
    const price = prices.length ? Math.min(...prices)
                : (window.STORE_CATALOG ? window.STORE_CATALOG.SEASONAL_CARD_PRICE : null);
    const win = R.AWARDS.win;
    const gold = R.DAILY_TIERS.gold;
    // "up to", because gold is the top tier and only the first win of the day at each tier pays.
    return (price != null ? 'Cards on this shelf cost ' + price + ' credits. ' : '') +
           'A win pays ' + win + ' credit' + (win === 1 ? '' : 's') +
           '. The daily challenge pays up to ' + gold + ' more.';
  }

  function renderStore() {
    const wrap = el('lib-store');
    if (!storeIsOpen() || !STATE.catalog.length) { wrap.hidden = true; return; }
    wrap.hidden = false;
    const rates = el('lib-store-rates');
    const sentence = ratesSentence();
    if (rates && sentence) rates.textContent = sentence;
    const shelfBox = el('lib-store-list');
    if (shelfBox) shelfBox.classList.toggle('is-rows', VIEW === 'rows');

    const list = forSaleIds();
    const total = STATE.catalog.length;
    el('lib-store-count').textContent = (total - list.length) + ' of ' + total;

    if (!list.length) {
      // The finished state is a sentence, not an empty box. A shelf that renders as nothing is
      // indistinguishable from a shelf that failed to load.
      //
      // AND IT SAYS WHAT THE BALANCE IS FOR, because for this player it is the only thing that
      // does. The season purchase is the entire credit sink in the system, so a player who has
      // bought the last card keeps earning against nothing: the balance climbs, the daily meter
      // fills, and both point at a shelf with nothing on it. That is the most engaged player there
      // is, being told the loop is over.
      //
      // WHAT THIS SENTENCE MAY PROMISE IS EXACTLY WHAT IS TRUE TODAY: credits and entitlements are
      // append-only and never expire (lib/store/standing.js computes the balance as the difference,
      // so nothing decays), which means a balance carried forward really will spend on whatever is
      // priced next. It deliberately does NOT name a Season 2, a date, or a feature -- none of
      // those have been decided, and a shelf that promises content is worse than one that promises
      // nothing. If the promise should be stronger or softer, this string is the only place to
      // change it.
      el('lib-store-list').innerHTML =
        '<p class="db-empty">Every card in this season is in your library. ' +
        'Credits keep counting — future seasons spend from the same balance.</p>';
    } else {
      el('lib-store-list').innerHTML =
        '<div class="db-rows">' + list.sort(byName).map(buyRowHtml).join('') + '</div>';
    }
    el('lib-store-msg').textContent = STATE.message;
  }

  // THE MECHANIC MENU, counted rather than merely listed — and the count is doing as much work as
  // the filtering. A player who has just been knocked Off-Balance and wants to know whether that is
  // a real part of the game or a one-card curiosity learns more from the "(42)" beside the option
  // than from the list it opens.
  //
  // COUNTED OVER THE TAB, NOT THE POOL, and that is a correction rather than a preference. Counting
  // the whole pool read "Chamber & Release (8)" and then filtered to seven, because the eighth is a
  // seasonal card this player has not bought — a control that disagrees with its own result by one
  // is a control a player stops trusting. It follows the tab and the purchase instead, which is
  // also the more useful number: it is a fact about THEIR cards.
  //
  // Deliberately not rebuilt on search. The counts answer "how much of my collection does this",
  // which the search box does not change, and options that renumbered on every keystroke would be
  // noise. The signature is what keeps it to the two events that do move it.
  let mechSig = null;
  function syncMechanicOptions() {
    const mech = el('lib-filter-mechanic');
    if (!mech) return;
    const sig = TAB + '|' + STATE.owned.length;
    if (sig === mechSig) return;
    mechSig = sig;
    const C = cardsTable();
    const ids = tabIds();
    const keep = mech.value || 'all';
    mech.innerHTML = '<option value="all">Any mechanic</option>' +
      MECHANIC_FILTERS.map(m => {
        const n = ids.filter(id => cardHasMechanic(C[id], m.key)).length;
        return '<option value="' + esc(m.key) + '">' + esc(m.label) + ' (' + n + ')</option>';
      }).join('');
    mech.value = keep;
    // A rebuild can orphan the current selection if the tab no longer has that option. Falling back
    // to 'all' in BOTH places, because a select whose value did not take reports '' and the filter
    // would then match nothing while the control looked fine.
    if (!mech.value) { mech.value = 'all'; FILTERS.mechanic = 'all'; }
  }

  // ---- the collection ----------------------------------------------------------------------------
  function renderCollection() {
    const C = cardsTable();
    syncMechanicOptions();
    const only = filterSet();
    const all = tabIds();
    const shown = all.filter(id => (!only || only.has(id)) && matchesSearch(id) &&
                                   (FILTERS.mechanic === 'all' || cardHasMechanic(C[id], FILTERS.mechanic)));

    const grouped = Object.create(null);
    shown.forEach(id => {
      const g = groupOf(C[id]);
      (grouped[g] || (grouped[g] = [])).push(id);
    });

    // Built as a QUEUE of group markup rather than one string, so the first paint can stop early
    // and the rest arrive as the reader approaches them. See drainInto below.
    PENDING = [];
    groupOrder().forEach(g => {
      const list = (grouped[g] || []).sort(byName);
      if (!list.length) return;
      PENDING.push('<div class="db-group"><h3 class="db-group-head">' + esc(label(g).toUpperCase()) +
        ' <span class="db-group-n">' + list.length + '</span></h3>' +
        '<div class="db-rows">' + list.map(id =>
          // THE SEASON'S CARDS ARE MARKED IN THE COLLECTION, and this is the other half of making a
          // purchase visible: the card does not merely appear somewhere in two hundred tiles, it
          // appears wearing the season's name. Nothing else in the library carries a state badge.
          '<div class="lib-cell">' +
            cardHtml(id, C[id].seasonal ? { stateText: 'Season 1', stateClass: 'season' } : null) +
          '</div>').join('') +
        '</div></div>');
    });

    const box = el('lib-list');
    box.classList.toggle('is-rows', VIEW === 'rows');
    if (!shown.length) {
      PENDING = [];
      box.innerHTML = '<p class="db-empty">No card here matches that. ' +
        '<button type="button" class="db-link" data-act="clear-filters">Clear filters</button></p>';
    } else {
      box.innerHTML = '';
      drainInto(box, EAGER_GROUPS);
    }
    // THE COUNT IS THE WHOLE FILTERED SET, not the mounted part. A number that grew as you
    // scrolled would be reporting on the renderer rather than on the collection.
    el('lib-count').textContent = shown.length === all.length
      ? String(all.length) : shown.length + ' of ' + all.length;
    // THE LIVE SENTENCE NAMES THE TAB, and that is not decoration. A role=status region is
    // announced when its text CHANGES; a player switching Collection -> All on an account that
    // owns nothing seasonal would have seen the same "194 cards" written twice and heard nothing
    // at all, from a control whose entire job is to change what is listed. The tab's own
    // aria-pressed flip is announced by the button, but it says "pressed", not what happened to
    // the list. Naming the tab makes the two cases textually different, so both get spoken.
    //
    // The name is VISUALLY HIDDEN and the number is not, because on screen the live tab is already
    // the lit chip two inches to the left; spelling it out again here would push a nowrap strip
    // that must survive 320px. Screen readers read the whole region, so they get the sentence.
    //
    // The view toggle deliberately does NOT write here: it changes the shape of each row, not
    // which cards are listed, and its aria-pressed pair already says which shape is live.
    const live = el('lib-result-count');
    if (live) {
      const tab = storeIsOpen() && TABS.filter(t => t.id === TAB)[0];
      const count = shown.length === all.length
        ? shown.length + ' cards'
        : shown.length + ' of ' + all.length + ' cards';
      live.innerHTML = (tab ? '<span class="visually-hidden">' + esc(tab.label) + ', </span>' : '') +
        esc(count);
    }
    // COLLECTION IS THE OWNED SUBSET; CARDS IS THE PLACE. Signed out, every card on this page is
    // one the game gives everybody, so calling it "yours" claimed a relationship that did not
    // exist -- and left no word for the seasonal cards, which are genuinely bought. The sitewide
    // audit flagged the mixture ("Do I own these, or am I browsing all cards?"). One heading, two
    // states, and the only one that says "your" is the one where something was acquired.
    const title = el('lib-collection-title');
    if (title) {
      title.childNodes[0].nodeValue = storeIsOpen() ? 'Your collection ' : 'All cards ';
    }
    syncViewButtons();
  }

  // ---------------- the card detail sheet ----------------
  //
  // The audit: "Scanning full rules requires reading every dense tile in the grid" -- there was no
  // way to look at ONE card. Rows made that worse by design (they carry no rules at all), so the
  // sheet is the other half of the same change rather than an addition to it.
  //
  // js/ui-sheet.js, which index.html has used since the deck preview, and which already does the
  // three things a hand-rolled overlay on this page would get wrong: Escape, a focus trap, and
  // focus restored to whatever opened it.
  function openCard(id) {
    const card = cardsTable()[id];
    if (!card || !window.UISheet) return false;
    const owned = ownedSet().has(id);
    const forSale = !owned && STATE.catalog.some(c => c.cardId === id);
    const price = window.STORE_CATALOG ? window.STORE_CATALOG.priceOf(id) : null;

    const actions = [];
    if (forSale && storeIsOpen()) {
      const afford = Number(STATE.balance) >= Number(price);
      actions.push({
        label: afford ? 'Buy — ' + price + ' credits' : 'Not enough credits',
        kind: 'primary',
        // Straight into the same buy() the shelf uses, so the repaint still comes from the
        // server's reply through applyState. There is deliberately no second purchase path.
        onClick: afford ? function () { buy(id); } : function () {}
      });
    }
    actions.push({ label: 'Close', kind: 'ghost' });

    window.UISheet.open({
      title: card.name,
      // THE EXPANDED CARD, THE SAME ONE THE BOUT DRAWS. This sheet used to render the COMPACT tile
      // with `rules: true`, because js/card-tile.js was the only renderer a page outside the game
      // shell could reach -- and the result was that the store, the one screen whose entire job is
      // helping someone decide whether to spend on a card, showed less of that card than any other
      // surface in the game. Open the same card from the Setup deck preview or from Zone 3 mid-bout
      // and you got two role panels, both banded figures, Coverage in words and Conditions as a
      // readable column; open it here and you got a 320px tile with a clamped gloss. Reported
      // 2026-08-18 in exactly those terms. js/card-detail.js exists so that both are now one
      // renderer -- see its header, and deploy_handoff/library-card-sheet-plan.md.
      //
      // NO ART PATH, and that is the honest answer rather than a missing feature: a card in the
      // collection belongs to no deck yet, so there is no fighter for the silhouettes to be drawn
      // on. `railDetailHtml(id, null)` is a supported call -- ui-silhouette.js documents `art: null`
      // as the built-in fallback figure, and the Setup deck preview passes null the same way for a
      // deck with no rangeFigure.
      //
      // THE TECHNIQUE WINDOW STAYS ON TOP OF IT. It is the one piece of move art on this page and it
      // was the best thing about the old sheet; the drawer has no window of its own. Its gloss is
      // the same sentence the drawer prints, which is why the page hides the drawer's copy (see
      // .card-sheet-detail .drawer-gloss in styles.css) rather than printing the line twice.
      //
      // Then the card's mechanics spelled out, between the face and its provenance: the badges say
      // WHICH Attack Type and Defense Type this card has, this says what they do, in the same words
      // the bout uses, from js/mechanics-dictionary.js.
      //
      // GUARDED, AND THE FALLBACK IS THE OLD SHEET. Every optional module on this page is loaded
      // this way -- the collection screen must keep working if a script tag is ever lost -- and
      // here that posture pays twice, because what it degrades to is precisely what shipped before.
      bodyHtml: (window.CardDetail
          ? '<div class="card-sheet-detail">' +
            window.CardTile.techniqueWindowHtml(card, {}) +
            window.CardDetail.railDetailHtml(id, null) +
            '</div>'
          : '<div class="lib-sheet-card">' +
            window.CardTile.compactCardHtml(id, { readonly: true, moveArt: true, rules: true }) +
            '</div>') +
        (window.MechanicsDictionary ? window.MechanicsDictionary.cardMechanicsHtml(card) : '') +
        provenanceHtml(id, owned, forSale, price),
      actions: actions
    });
    return true;
  }

  // Where a card came from and whether it is yours -- the ownership question the audit found this
  // page answering ambiguously. Says nothing about buying when there is no store to buy from.
  function provenanceHtml(id, owned, forSale, price) {
    const card = cardsTable()[id];
    // The tier's PLAYER-FACING word: 'Family' is the identifier in card data and 'Foundation' is
    // what a reader sees, because "Family" already meant an Attack Type and a Defense Type in this
    // game. tierLabel in js/card-tile.js owns the mapping; asked rather than copied.
    const tierWord = (window.CardTile && window.CardTile.tierLabel)
      ? window.CardTile.tierLabel(card.ownershipTier) : card.ownershipTier;
    const bits = ['<span class="lib-prov-tier">' + esc(tierWord) + '</span>'];
    if (card.seasonal) {
      bits.push('<span class="lib-prov-season">Scenes from Summer</span>');
      if (owned) bits.push('<span class="lib-prov-owned">In your collection</span>');
      else if (forSale && storeIsOpen()) bits.push('<span class="lib-prov-price">' + price + ' credits</span>');
    } else {
      bits.push('<span class="lib-prov-owned">Every player has this one</span>');
    }
    return '<p class="lib-prov">' + bits.join('') + '</p>';
  }

  function render() {
    renderStanding();
    renderStore();
    renderTabs();
    renderCollection();
    // The balance in the top strip, written by the module the deck builder also uses so the two
    // pages cannot word it differently. It decides for itself whether there is an account to
    // report on, so it is called unconditionally.
    if (window.PageNav) window.PageNav.render(STATE);
  }

  // ---------------- the server's answer ----------------
  //
  // THE ONLY WRITER OF owned/balance/configured/signedIn, and it takes the same shape from both of
  // its callers: the page-load read of GET /api/store, and the reply to POST /api/store/purchase
  // (which returns the whole standing recomputed after the write, precisely so this can be true).
  //
  // One function, so a screen painted by a purchase and a screen painted by a reload cannot differ.
  // The alternative — patching `owned` where the click is handled — renders a card the player owns
  // only if the write actually landed, and the write is the thing that might not have.
  function applyState(s) {
    STATE.asked = true;
    if (typeof s.ok === 'boolean') STATE.reachable = s.ok;
    STATE.configured = !!s.configured;
    STATE.signedIn = !!s.signedIn;
    if (Array.isArray(s.owned)) STATE.owned = s.owned.slice();
    if (typeof s.balance === 'number') STATE.balance = s.balance;
    if (Array.isArray(s.catalog)) STATE.catalog = s.catalog.slice();
    render();
  }

  async function refresh() {
    if (!window.StoreClient) return null;
    const s = await window.StoreClient.state();
    applyState(s);
    return s;
  }

  // ---------------- buying ----------------

  async function buy(cardId) {
    if (!window.StoreClient || STATE.busy) return null;
    if (!storeIsOpen()) return null;
    // Refused here as well as disabled in the markup, because a disabled button and a call that
    // refuses are two separate claims — the builder's rule, and the one that matters most on the
    // single action in this game that spends something.
    const price = priceOf(cardId);
    if (price == null || ownedSet().has(cardId) || STATE.balance < price) return null;

    STATE.busy = cardId;
    STATE.message = '';
    render();

    let res;
    try { res = await window.StoreClient.buy(cardId); }
    catch (e) { res = { ok: false, error: 'unexpected' }; }
    STATE.busy = null;

    const name = (cardsTable()[cardId] || {}).name || cardId;
    if (res && res.ok) {
      STATE.message = res.bought ? name + ' is in your library.'
        : res.already ? 'You already own ' + name + '.'
        : res.insufficient ? 'You do not have enough credits for ' + name + ' yet.'
        : 'Nothing was bought.';
      // EVEN A REFUSED PURCHASE REPAINTS. `bought: false` still carries the server's owned set and
      // balance, and applying it is how a screen that had drifted — a card bought in another tab,
      // credits earned in another window — corrects itself at the moment the player finds out.
      applyState({ configured: STATE.configured, signedIn: true, owned: res.owned,
                   balance: res.balance, catalog: STATE.catalog });
      return res;
    }

    if (res && res.error === 'not_signed_in') {
      STATE.message = 'You are signed out. Sign in again to buy.';
      applyState({ configured: STATE.configured, signedIn: false, owned: [],
                   balance: 0, catalog: STATE.catalog });
      return res;
    }
    // NOT "NOTHING WAS BOUGHT", WHICH THIS CANNOT KNOW. A request that fails on the way back is
    // indistinguishable from one that failed on the way out: the write may have landed and the
    // response been lost — that is the ordinary dropped-response case the endpoint's primary key
    // exists to make safe, not a rare one. Claiming nothing happened would be a guess presented as
    // a fact, about money.
    //
    // So the screen says what it knows and then ASKS THE SERVER, which is the only thing that can
    // settle it. If that read also fails the collection stays as it was and the message stands.
    STATE.message = 'The store did not answer, so this purchase is unconfirmed. ' +
                    'Your library below is whatever the server last told us.';
    render();
    await refresh();
    return res;
  }

  // ---------------- wiring ----------------

  function onClick(e) {
    const t = e.target.closest ? e.target.closest('[data-act]') : null;
    if (!t) return;
    const act = t.getAttribute('data-act');
    if (act === 'buy') buy(t.getAttribute('data-card-id'));
    else if (act === 'tab') setTab(t.getAttribute('data-tab'));
    else if (act === 'view-rows') setView('rows');
    else if (act === 'view-tiles') setView('tiles');
    else if (act === 'clear-filters') {
      FILTERS.search = '';
      FILTERS.style = 'all';
      FILTERS.mechanic = 'all';
      el('lib-search').value = '';
      el('lib-filter-style').value = 'all';
      if (el('lib-filter-mechanic')) el('lib-filter-mechanic').value = 'all';
      render();
    }
  }

  function buildStaticControls() {
    const sel = el('lib-filter-style');
    sel.innerHTML = '<option value="all">Every card</option>' +
      (window.STYLES || []).map(s =>
        '<option value="' + esc(s) + '">For a ' + esc(label(s)) + ' deck</option>').join('');
    // THE JUMP MENU. The audit asked for a "style jump menu" beside the sticky filters, and on a
    // page this long it is the difference between finding Judo and scrolling for it.
    const jump = el('lib-jump');
    if (jump) {
      jump.innerHTML = '<option value="">Jump to…</option>' +
        groupOrder().map(g => '<option value="' + esc(g) + '">' + esc(label(g)) + '</option>').join('');
    }
  }

  // Marks whichever view button is live. aria-pressed rather than a class alone, because these are
  // two states of one choice and that is what a screen reader needs told.
  function syncViewButtons() {
    const map = { 'view-rows': 'rows', 'view-tiles': 'tiles' };
    Object.keys(map).forEach(act => {
      const btn = document.querySelector('[data-act="' + act + '"]');
      if (!btn) return;
      const on = VIEW === map[act];
      btn.setAttribute('aria-pressed', String(on));
      btn.classList.toggle('is-on', on);
    });
  }

  function wire() {
    document.addEventListener('click', onClick);
    el('lib-search').addEventListener('input', e => {
      FILTERS.search = e.target.value.trim();
      renderCollection();
    });
    el('lib-filter-style').addEventListener('change', e => {
      FILTERS.style = e.target.value;
      renderCollection();
    });
    const mech = el('lib-filter-mechanic');
    if (mech) mech.addEventListener('change', e => {
      FILTERS.mechanic = e.target.value;
      renderCollection();
    });
    const jump = el('lib-jump');
    if (jump) jump.addEventListener('change', e => {
      const g = e.target.value;
      e.target.value = '';
      if (!g) return;
      // The group may still be in the queue, so everything is mounted before anything is sought:
      // scrollIntoView on an element that does not exist yet is a silent no-op.
      drainAll();
      const heads = [].slice.call(document.querySelectorAll('#lib-list .db-group-head'));
      const want = label(g).toUpperCase();
      const head = heads.filter(h => h.textContent.trim().indexOf(want) === 0)[0];
      if (head) head.scrollIntoView({ block: 'start' });
    });
  }

  function init() {
    VIEW = preferredView();
    buildStaticControls();
    wire();
    // PAINTED BEFORE ANYTHING IS ASKED. The base library needs no network at all, so a `file://`
    // copy and a slow connection both get a complete, correct screen immediately; the season's
    // shelf appears underneath it a moment later, or never, and neither case is an error state.
    render();
    refresh();
  }

  window.Library = {
    init: init,
    // Driven through these in tests rather than through synthesised clicks, the same arrangement
    // js/deck-builder-ui.js records: what has to be asserted is the STATE, and "the button renders
    // disabled" and "the call refuses" are two claims that both need checking.
    refresh: refresh,
    buy: buy,
    applyState: applyState,
    state: () => ({
      asked: STATE.asked, configured: STATE.configured, signedIn: STATE.signedIn,
      owned: STATE.owned.slice(), balance: STATE.balance, busy: STATE.busy,
      message: STATE.message, catalog: STATE.catalog.slice(), reachable: STATE.reachable
    }),
    collectionIds: collectionIds,
    forSaleIds: forSaleIds,
    storeIsOpen: storeIsOpen,
    setFilter: (k, v) => { FILTERS[k] = v; render(); },
    // The view mode and the sheet, exported for the same reason everything else here is: the
    // claims are about state, and a synthesised click proves the wiring rather than the rule.
    setView: setView,
    setTab: setTab,
    tab: () => TAB,
    view: () => VIEW,
    openCard: openCard,
    // The queue, so a suite can assert that the first paint is BOUNDED and that draining it
    // yields the whole collection.
    pendingGroups: () => PENDING.length,
    drainAll: drainAll,
    // The drain step itself, exported so the browser tier can drive it. Verified in headless
    // Chrome by calling this with the sentinel in reach: one group mounts per call and the queue
    // shortens. The SCROLL that normally calls it could not be driven there -- a synthetic
    // scrollTop under --virtual-time-budget delivers no event -- which is why the mechanism is
    // reachable by name rather than only by gesture.
    drainStep: maybeDrain
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
